import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { toPng } from "html-to-image";
import { Download, FileText, MapPin, MousePointerClick, Share2 } from "lucide-react";
import { WHATSAPP_DISPLAY } from "../lib/whatsapp";

const API = process.env.REACT_APP_BACKEND_URL;

const partagerAffiche = async (label, file) => {
  const url = `${window.location.origin}/downloads/${file}`;
  const texte = `${label} — FK Conseil & Événement`;
  if (navigator.share) {
    try {
      await navigator.share({ title: "FK Conseil & Événement", text: texte, url });
      return;
    } catch (e) {
      /* annulé par l'utilisateur */
    }
  }
  window.open(`https://wa.me/?text=${encodeURIComponent(`${texte}\n${url}`)}`, "_blank", "noopener,noreferrer");
};

const PHOTO = "/images/jb-nouveau-hd.jpg";

const Proximite = ({ className }) => (
  <div
    className={`inline-flex animate-pulse items-center gap-2 rounded-full px-5 py-2 text-[10px] font-bold uppercase tracking-[0.2em] ${className}`}
    data-testid="affiche-proximite"
  >
    <MapPin className="h-3.5 w-3.5" />
    À 10 min de l'Accor Arena
  </div>
);

const Chips = ({ chip }) => (
  <div className="flex flex-wrap items-center justify-center gap-2" data-testid="affiche-ambiance">
    <span className={`rounded-full px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] ${chip}`}>Soirée animée</span>
    <span className={`rounded-full px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] ${chip}`}>Soirée au rendez-vous</span>
  </div>
);

const PhotoBg = ({ overlay }) => (
  <>
    <img src={PHOTO} alt="JB Mpiana" className="absolute inset-0 h-full w-full object-cover object-top" data-testid="affiche-photo" />
    {overlay}
  </>
);

/* ============ AFFICHE 1 — BLANC PUR (photo pleine page, fond blanc) ============ */
const Poster1 = () => (
  <div className="affiche-capture relative mx-auto aspect-[3/4] w-full max-w-[440px] overflow-hidden rounded-2xl shadow-2xl">
    <PhotoBg />
    {[...Array(10)].map((_, i) => (
      <span key={i} className="pointer-events-none absolute z-10 h-1.5 w-1.5 rounded-full bg-[#c9a227]"
        style={{ left: `${(i * 43 + 7) % 100}%`, top: `${(i * 31 + 5) % 60}%`, animation: `floaty ${3 + (i % 3)}s ease-in-out ${i * 0.4}s infinite`, boxShadow: "0 0 10px 2px rgba(201,162,39,0.7)" }} />
    ))}
    <div className="absolute inset-x-0 top-0 z-10 bg-white/85 px-6 py-4 text-center backdrop-blur-sm">
      <p className="text-[10px] font-bold uppercase tracking-[0.4em] text-[#aa8c2c]">FK Conseil &amp; Événement</p>
    </div>
    <div className="absolute inset-x-0 bottom-0 z-10 bg-white/90 px-6 pb-5 pt-4 text-center backdrop-blur-sm">
      <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
        className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#141414]">
        Samedi <span className="font-display text-2xl normal-case text-[#c9a227]">14</span> Novembre — jusqu'à 5h
      </motion.p>
      <h1 className="mt-1 font-display text-4xl leading-none tracking-tight text-[#141414]">AFTER SOIRÉE <span className="text-[#c9a227]">VIP</span></h1>
      <p className="mt-1 text-[11px] font-bold uppercase tracking-[0.2em] text-[#141414]">Chez Ousman Gouzele</p>
      <div className="mt-2.5"><Proximite className="bg-[#141414] text-[#d4af37]" /></div>
      <div className="mt-2"><Chips chip="border border-[#c9a227]/50 text-[#aa8c2c]" /></div>
      <p className="mt-2.5 text-[10px] text-[#141414]/70">Factory58 — 12 rue Gutenberg, 93000 Bobigny</p>
      <p className="mt-2.5 text-[9px] font-bold uppercase tracking-[0.15em] text-[#aa8c2c]">Réservation en ligne · WhatsApp : {WHATSAPP_DISPLAY}</p>
      <span data-testid="affiche-site-link"
        className="mt-2 inline-block rounded-full border-2 border-[#c9a227] px-6 py-1.5 font-display text-xl tracking-tight text-[#141414]">
        www.fk-evenement.fr
      </span>
    </div>
  </div>
);

/* ============ AFFICHE 2 — BLEU FÊTE (dégradé bleu vif bas) ============ */
const Poster2 = () => (
  <div className="affiche-capture relative mx-auto aspect-[3/4] w-full max-w-[440px] overflow-hidden rounded-2xl shadow-2xl">
    <PhotoBg overlay={<div className="absolute inset-0 bg-gradient-to-t from-[#1d4ed8] via-[#1d4ed8]/60 to-transparent" style={{ backgroundSize: "100% 65%", backgroundPosition: "bottom", backgroundRepeat: "no-repeat" }} />} />
    {[...Array(14)].map((_, i) => (
      <span key={i} className="confetti pointer-events-none absolute z-10"
        style={{ left: `${(i * 39) % 100}%`, width: "7px", height: "11px", borderRadius: i % 2 ? "50%" : "2px",
          background: ["#ffffff", "#60a5fa", "#d4af37", "#93c5fd"][i % 4], animationDuration: `${4 + (i % 4)}s`, animationDelay: `${(i % 6) * 0.5}s`, opacity: 0.8 }} />
    ))}
    <div className="absolute inset-x-0 top-0 z-10 px-6 py-4 text-center">
      <p className="inline-block rounded-full bg-[#1d4ed8] px-5 py-1.5 text-[10px] font-bold uppercase tracking-[0.4em] text-white">FK Conseil &amp; Événement</p>
    </div>
    <div className="absolute inset-x-0 bottom-0 z-10 px-6 pb-5 pt-8 text-center text-white">
      <motion.div initial={{ opacity: 0, scale: 0.8 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.7, type: "spring", bounce: 0.4 }}>
        <p className="text-[10px] font-bold uppercase tracking-[0.3em]">Samedi <span className="font-display text-2xl normal-case text-[#fbbf24]">14</span> Novembre — jusqu'à 5h</p>
      </motion.div>
      <h1 className="mt-1 font-sans text-4xl font-black leading-none tracking-tight">AFTER SOIRÉE <span className="text-[#fbbf24]">VIP</span></h1>
      <p className="mt-1 text-[11px] font-bold uppercase tracking-[0.2em]">Chez Ousman Gouzele</p>
      <div className="mt-2.5"><Proximite className="bg-white text-[#1d4ed8]" /></div>
      <div className="mt-2"><Chips chip="bg-white/20 text-white" /></div>
      <p className="mt-2.5 text-[10px] text-white/80">Factory58 — 12 rue Gutenberg, 93000 Bobigny</p>
      <p className="mt-2.5 text-[9px] font-bold uppercase tracking-[0.15em] text-[#fbbf24]">Réservation en ligne · WhatsApp : {WHATSAPP_DISPLAY}</p>
      <span data-testid="affiche-site-link"
        className="mt-2 inline-block rounded-full border-2 border-white px-6 py-1.5 font-sans text-xl font-black tracking-tight text-white">
        www.fk-evenement.fr
      </span>
    </div>
  </div>
);

/* ============ AFFICHE 3 — ROUGE PASSION (assorti à la tenue) ============ */
const Poster3 = () => (
  <div className="affiche-capture relative mx-auto aspect-[3/4] w-full max-w-[440px] overflow-hidden rounded-2xl shadow-2xl">
    <PhotoBg overlay={<div className="absolute inset-0 bg-gradient-to-t from-[#b91c1c] via-[#7f1d1d]/70 to-transparent" style={{ backgroundSize: "100% 70%", backgroundPosition: "bottom", backgroundRepeat: "no-repeat" }} />} />
    <div className="pointer-events-none absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-white/70 to-transparent" />
    {[...Array(10)].map((_, i) => (
      <span key={i} className="pointer-events-none absolute z-10 h-2 w-2 rotate-45 bg-[#fbbf24]"
        style={{ left: `${(i * 47 + 11) % 100}%`, top: `${(i * 29 + 8) % 55}%`, animation: `floaty ${2.5 + (i % 3)}s ease-in-out ${i * 0.35}s infinite`, boxShadow: "0 0 12px 3px rgba(251,191,36,0.8)" }} />
    ))}
    <div className="absolute inset-x-0 top-0 z-10 px-6 py-4 text-center">
      <p className="text-[10px] font-bold uppercase tracking-[0.4em] text-[#7f1d1d]">FK Conseil &amp; Événement</p>
    </div>
    <div className="absolute inset-x-0 bottom-0 z-10 px-6 pb-5 pt-8 text-center text-white">
      <p className="text-[10px] font-bold uppercase tracking-[0.3em]">Samedi <span className="font-display text-2xl normal-case text-[#fbbf24]">14</span> Novembre — jusqu'à 5h</p>
      <h1 className="mt-1 font-display text-4xl leading-none tracking-tight">AFTER SOIRÉE <span className="text-[#fbbf24]">VIP</span></h1>
      <p className="mt-1 text-[11px] font-bold uppercase tracking-[0.2em]">Chez Ousman Gouzele</p>
      <div className="mt-2.5"><Proximite className="bg-[#fbbf24] text-[#7f1d1d]" /></div>
      <div className="mt-2"><Chips chip="bg-white/20 text-white" /></div>
      <p className="mt-2.5 text-[10px] text-white/85">Factory58 — 12 rue Gutenberg, 93000 Bobigny</p>
      <p className="mt-2.5 text-[9px] font-bold uppercase tracking-[0.15em] text-[#fbbf24]">Réservation en ligne · WhatsApp : {WHATSAPP_DISPLAY}</p>
      <span data-testid="affiche-site-link"
        className="mt-2 inline-block rounded-full border-2 border-[#fbbf24] px-6 py-1.5 font-display text-xl tracking-tight text-white">
        www.fk-evenement.fr
      </span>
    </div>
  </div>
);

/* ============ AFFICHE 4 — OR CHAMPAGNE (cadre doré lumineux) ============ */
const Poster4 = () => (
  <div className="affiche-capture relative mx-auto aspect-[3/4] w-full max-w-[440px] overflow-hidden rounded-2xl border-4 border-[#c9a227] bg-[#f8f1de] shadow-2xl">
    <div className="absolute inset-0 p-3">
      <img src={PHOTO} alt="JB Mpiana" className="h-[46%] w-full rounded-t-xl object-cover object-top" data-testid="affiche-photo" />
    </div>
    {[...Array(12)].map((_, i) => (
      <span key={i} className="pointer-events-none absolute z-10 h-1.5 w-1.5 rounded-full bg-[#c9a227]"
        style={{ left: `${(i * 41 + 13) % 100}%`, top: `${(i * 23 + 60) % 40}%`, animation: `floaty ${3 + (i % 4)}s ease-in-out ${i * 0.3}s infinite` }} />
    ))}
    <div className="absolute inset-x-0 top-3 z-10 bg-[#141414]/85 px-6 py-2.5 text-center">
      <p className="text-[10px] font-bold uppercase tracking-[0.4em] text-[#d4af37]">FK Conseil &amp; Événement</p>
    </div>
    <div className="absolute inset-x-0 bottom-0 z-10 bg-[#f8f1de] px-6 pb-4 pt-3 text-center">
      <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#141414]">Samedi <span className="font-display text-2xl normal-case text-[#aa8c2c]">14</span> Novembre — jusqu'à 5h</p>
      <h1 className="mt-0.5 font-display text-3xl leading-none tracking-tight text-[#141414]">AFTER SOIRÉE <span className="shimmer text-[#aa8c2c]">VIP</span></h1>
      <p className="mt-1 text-[11px] font-bold uppercase tracking-[0.2em] text-[#141414]">Chez Ousman Gouzele</p>
      <div className="mt-2"><Proximite className="bg-[#141414] text-[#d4af37]" /></div>
      <div className="mt-1.5"><Chips chip="border border-[#c9a227]/60 text-[#aa8c2c]" /></div>
      <p className="mt-2 text-[10px] text-[#141414]/70">Factory58 — 12 rue Gutenberg, 93000 Bobigny</p>
      <p className="mt-2 text-[9px] font-bold uppercase tracking-[0.15em] text-[#aa8c2c]">Réservation en ligne · WhatsApp : {WHATSAPP_DISPLAY}</p>
      <span data-testid="affiche-site-link"
        className="mt-1.5 inline-block rounded-full border-2 border-[#c9a227] px-6 py-1.5 font-display text-xl tracking-tight text-[#141414]">
        www.fk-evenement.fr
      </span>
    </div>
  </div>
);

/* ============ AFFICHE 5 — SOLEIL PARTY (orange/corail vitaminé) ============ */
const Poster5 = () => (
  <div className="affiche-capture relative mx-auto aspect-[3/4] w-full max-w-[440px] overflow-hidden rounded-2xl shadow-2xl">
    <PhotoBg overlay={<div className="absolute inset-0 bg-gradient-to-t from-[#f97316] via-[#fb923c]/55 to-transparent" style={{ backgroundSize: "100% 68%", backgroundPosition: "bottom", backgroundRepeat: "no-repeat" }} />} />
    {[...Array(12)].map((_, i) => (
      <span key={i} className="confetti pointer-events-none absolute z-10 rounded-full"
        style={{ left: `${(i * 37 + 5) % 100}%`, width: `${6 + (i % 3) * 3}px`, height: `${6 + (i % 3) * 3}px`,
          background: ["#ffffff", "#fde047", "#fb7185"][i % 3], animationDuration: `${4.5 + (i % 4)}s`, animationDelay: `${(i % 6) * 0.5}s`, opacity: 0.85 }} />
    ))}
    <div className="absolute inset-x-0 top-0 z-10 px-6 py-4 text-center">
      <p className="inline-block rounded-full bg-[#f97316] px-5 py-1.5 text-[10px] font-bold uppercase tracking-[0.4em] text-white">FK Conseil &amp; Événement</p>
    </div>
    <div className="absolute inset-x-0 bottom-0 z-10 px-6 pb-5 pt-8 text-center text-white">
      <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}>
        <p className="text-[10px] font-bold uppercase tracking-[0.3em]">Samedi <span className="font-display text-2xl normal-case text-[#fde047]">14</span> Novembre — jusqu'à 5h</p>
      </motion.div>
      <h1 className="mt-1 font-sans text-4xl font-black leading-none tracking-tight">AFTER SOIRÉE <span className="text-[#fde047]">VIP</span></h1>
      <p className="mt-1 text-[11px] font-bold uppercase tracking-[0.2em]">Chez Ousman Gouzele</p>
      <div className="mt-2.5"><Proximite className="bg-white text-[#ea580c]" /></div>
      <div className="mt-2"><Chips chip="bg-white/20 text-white" /></div>
      <p className="mt-2.5 text-[10px] text-white/85">Factory58 — 12 rue Gutenberg, 93000 Bobigny</p>
      <p className="mt-2.5 text-[9px] font-bold uppercase tracking-[0.15em] text-[#fde047]">Réservation en ligne · WhatsApp : {WHATSAPP_DISPLAY}</p>
      <span data-testid="affiche-site-link"
        className="mt-2 inline-block rounded-full border-2 border-white px-6 py-1.5 font-sans text-xl font-black tracking-tight text-white">
        www.fk-evenement.fr
      </span>
    </div>
  </div>
);

const VARIANTS = [
  { id: "blanc", label: "Affiche 1 — Blanc Pur", Poster: Poster1 },
  { id: "bleu", label: "Affiche 2 — Bleu Fête", Poster: Poster2 },
  { id: "rouge", label: "Affiche 3 — Rouge Passion", Poster: Poster3 },
  { id: "or", label: "Affiche 4 — Or Champagne", Poster: Poster4 },
  { id: "soleil", label: "Affiche 5 — Soleil Party", Poster: Poster5 },
];

const PosterBlock = ({ id, label, Poster }) => {
  const ref = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const handleDownload = async () => {
    if (!ref.current || downloading) return;
    setDownloading(true);
    try {
      const dataUrl = await toPng(ref.current, { pixelRatio: 3, cacheBust: true });
      const link = document.createElement("a");
      link.download = `affiche-soiree-vip-14-novembre-${id}.png`;
      link.href = dataUrl;
      link.click();
    } catch (e) {
      // silencieux
    } finally {
      setDownloading(false);
    }
  };
  return (
    <div className="flex flex-col items-center" data-testid={`affiche-block-${id}`}>
      <p className="mb-4 text-xs font-bold uppercase tracking-[0.3em] text-gold">{label}</p>
      <a
        href="https://fk-evenement.fr/soiree-vip"
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`affiche-click-${id}`}
        className="block w-full max-w-[440px] cursor-pointer transition-transform duration-300 hover:scale-[1.02]"
        title="Cliquez pour réserver sur fk-evenement.fr"
      >
        <div ref={ref} className="pointer-events-none w-full">
          <Poster />
        </div>
      </a>
      <p className="mt-3 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-white/40">
        <MousePointerClick className="h-3.5 w-3.5 text-gold" />
        1 clic sur l'affiche = réservation en ligne
      </p>
      <a
        href="https://fk-evenement.fr/soiree-vip"
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`affiche-visit-${id}`}
        className="mt-3 flex items-center gap-2 rounded-full border-2 border-gold px-7 py-3 text-[11px] font-bold uppercase tracking-[0.2em] text-gold transition-colors duration-300 hover:bg-gold hover:text-[#0a0a0a]"
      >
        <MousePointerClick className="h-4 w-4" />
        Visiter www.fk-evenement.fr
      </a>
      <button
        type="button"
        onClick={handleDownload}
        disabled={downloading}
        data-testid={`affiche-download-${id}`}
        className="mt-5 flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-[11px] font-bold uppercase tracking-[0.2em] text-[#0a0a0a] transition-transform duration-300 hover:scale-[1.03] disabled:opacity-50"
      >
        <Download className="h-4 w-4" />
        {downloading ? "Téléchargement..." : "Télécharger cette affiche"}
      </button>
      <div className="mt-3 flex items-center gap-3">
        <a
          href={`${API}/api/affiche-pdf/affiche-soiree-vip-14-novembre-${id}.png`}
          data-testid={`affiche-pdf-${id}`}
          className="flex items-center gap-2 rounded-full border border-white/25 px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.18em] text-white/70 transition-colors duration-300 hover:border-gold hover:text-gold"
        >
          <FileText className="h-3.5 w-3.5" />
          PDF
        </a>
        <button
          type="button"
          onClick={() => partagerAffiche(label, `affiche-soiree-vip-14-novembre-${id}.png`)}
          data-testid={`affiche-share-${id}`}
          className="flex items-center gap-2 rounded-full border border-white/25 px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.18em] text-white/70 transition-colors duration-300 hover:border-gold hover:text-gold"
        >
          <Share2 className="h-3.5 w-3.5" />
          Partager
        </button>
      </div>
    </div>
  );
};

export default function AfficheVip() {
  return (
    <div className="min-h-screen bg-[#141414] px-6 py-20" data-testid="affiche-page">
      <style>{`
        @keyframes floaty { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-12px); } }
        @keyframes confetti-fall { 0% { transform: translateY(-8vh) rotate(0deg); } 100% { transform: translateY(110vh) rotate(360deg); } }
        .confetti { top: -40px; animation: confetti-fall linear infinite; }
        @keyframes shimmer { 0%, 100% { opacity: 1; } 50% { opacity: 0.55; } }
        .shimmer { animation: shimmer 2s ease-in-out infinite; }
      `}</style>
      <div className="mx-auto max-w-7xl">
        <div className="text-center">
          <h1 className="font-display text-4xl tracking-tight text-white sm:text-5xl">
            Choisissez votre <em className="italic text-gold">affiche</em>
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-sm text-white/50">
            5 modèles animés avec la photo en pleine page. Le lien www.fk-evenement.fr est cliquable sur la version web ;
            téléchargez votre préférée en image pour vos réseaux sociaux.
          </p>
        </div>
        <div className="mt-14 grid items-start gap-14 sm:grid-cols-2 lg:grid-cols-3 lg:gap-10">
          {VARIANTS.map((v) => (
            <PosterBlock key={v.id} {...v} />
          ))}
        </div>
        <p className="mt-14 text-center">
          <a href="/" className="text-[10px] uppercase tracking-[0.3em] text-white/40 transition-colors hover:text-gold">
            ← Retour au site
          </a>
        </p>
      </div>
    </div>
  );
}
